/****************
 * WHAT THE BROWSER REMEMBERS, AND WHO IT BELONGS TO.
 *
 * Signing out already clears the query cache and forgets which user we are
 * (AuthContext). What it did not clear is anything written to localStorage,
 * and one of those keys is the Orb conversation: on the shared machines the
 * beta is being demoed on, the next person to sign in could read the previous
 * person's chat with the assistant. M-23, 2026-09-20.
 *
 * TWO KINDS OF KEY, AND THE DIFFERENCE MATTERS.
 *
 * PERSONAL is anything that belongs to the human who was signed in: their
 * conversation, how far they had read, which of their broker accounts was
 * selected. Signing out forgets all of it.
 *
 * DEVICE is a preference of the browser rather than the person -- how big they
 * dragged the chat window, which rows the price table shows, a banner they
 * dismissed. Clearing those on sign-out would punish the owner of the laptop
 * for signing out, and none of it says anything about the account.
 *
 * Every key is in exactly one list, and a test fails on any key that is in
 * neither -- because the failure mode here is silent: a new key gets added,
 * nobody thinks about sign-out, and it leaks for months without a symptom.
 ****************/

// Keys belonging to the signed-in person. Cleared on sign-out.
export const PERSONAL_KEYS = [
  'copyfi.orb.conversation',
  // How many times this person left the Strategy Builder halfway (pages/VirginDashboard.tsx, 10-25).
  'copyfi.builder.abandons',
  // Trader vs Trader: whom this person is comparing (lib/compareTraders.ts).
  'copyfi.compare.traders',
  // Backtest vs Backtest: which of this person's backtests (lib/compareBacktests.ts).
  'copyfi.compare.backtests',
  // The tier an admin is looking through (contexts/AuthContext.tsx).
  'copyfi.tierLens',
  'copyfi.notifications.lastSeenId',
  // Competition mode: the next session this person starts is their competition
  // entry (lib/competition.ts). Someone else signing in must not inherit it.
  'copyfi.competitionMode',
  // Whether this person has been shown the competition's short video.
  'copyfi.competitionIntroSeen',
  // The admin's Speed run (lib/speedRun.ts): on while this person is pretending to be new, and the
  // app mode to go back to. Someone else signing in must not land in it (live review #143).
  'copyfi.speedRun',
  'copyfi.speedRun.modeBefore',
  // The achievements followed or shown DURING a run, kept here instead of on the account (round 9).
  'copyfi.speedRun.achievements',
  // Whether this run's first session has started: the Pro switch, broker keys and Telegram open then (10-31).
  'copyfi.speedRun.done',
  // The A-to-Z path (11-01): its step, the signals seen, the trader and strategy picked, the traders starred.
  'copyfi.speedRun.step',
  'copyfi.speedRun.seen',
  'copyfi.speedRun.picks',
  'copyfi.speedRun.favorites',
  // The favourites this person had before the run, so leaving it removes only the run's own stars.
  'copyfi.speedRun.favoritesBefore',
  // Which backtest-ready notices this person has already seen in the bell (hooks/useNotices.ts).
  'copyfi.notices.seen',
  // The Orb's conversation during a run (its own, never the account's).
  'copyfi.speedRun.orb',
  // The run's backtest failed: its backtest step says so until a new one starts.
  'copyfi.speedRun.backtestFailed',
  // What the run's own requests created: leaving the run deletes those ids and nothing else (11-03).
  'copyfi.speedRun.made',
  // A useSyncedPreference key: the server holds it PER USER and this is that
  // person's cached copy, so it is theirs however local it looks.
  'copyfi.topTraders.filterPresets',

  /****************
   * THE REMEMBERED DASHBOARD CONTROLS. 2026-09-23.
   *
   * Guillaume asked for every button on the dashboard to still be where he
   * left it. All of these go through useSyncedPreference, so the SERVER holds
   * them per user and what is in localStorage is that person's cached copy --
   * the same reason filterPresets above is personal rather than device.
   *
   * None of them says anything interesting about an account, and clearing
   * them costs a signed-out browser nothing but a return to defaults. But on
   * the shared machines the beta is demoed on, the next person to sign in
   * would otherwise open on the previous person's venue, period and chosen
   * metrics, and spend a while wondering who picked them.
   *
   * NOTE, not changed here: copyfi.topTraders.columns below is also a
   * useSyncedPreference key and is classified DEVICE. The two rules disagree
   * on that one key. Left alone rather than quietly reclassified in a commit
   * about page speed -- it is on the list.
   ****************/
  'copyfi.dashboard.period',
  // AI › MiroFish (2026-10-23): the Top Traders columns added beside its own
  // (useSyncedPreference, per user), and the last screen run, kept for the
  // trip to a trader's profile and back (now kept on the server instead).
  'copyfi.mirofish.cols',
  // The chat boxes open at the bottom of the page (components/community/ChatDock.tsx,
  // 2026-10-24): whose conversations, so it goes with the account. sessionStorage.
  'copyfi.chat.boxes',
  'copyfi.dashboard.sortBy',
  'copyfi.dashboard.sortDesc',
  'copyfi.dashboard.favouritesOnly',
  'copyfi.dashboard.tradeView',
  'copyfi.dashboard.tradePeriod',
  'copyfi.traders.dataMode',
  'copyfi.traders.period',
  'copyfi.traders.venue',
  'copyfi.traders.topAssetMetric',
  'copyfi.traders.longShortMetric',
  // .v2 since 2026-10-19: new defaults (figures shown nowhere else on the page) reach everyone once.
  'copyfi.traders.visibleMetrics.v2',
  // The session page's own list since review #85 (10-26).
  'copyfi.sessions.visibleMetrics.v1',
  // Whose positioning the Traders and Asset pages show (groups and categories, 10-26).
  'copyfi.traderSets.lens',
  'copyfi.traders.tradeDirFilter',
  'copyfi.traders.tradeSymFilter',
  // Asset type / broker availability filters (2026-10-04).
  'copyfi.traders.assetFilter',
  'copyfi.dashboard.leaderAssets',
  'copyfi.dashboard.tradeAssets',
  'copyfi.dashboard.portfolioAssetClass',
  'copyfi.traders.allAssetClass',
  'copyfi.assets.class',
  'copyfi.assets.broker',
  'copyfi.assets.sort',
  'copyfi.assets.favourites',
  'copyfi.assets.favouritesOnly',
  // An asset's traders: the columns shown and the sort (2026-10-05).
  'copyfi.assetTraders.columns',
  'copyfi.assetTraders.sort',
  'copyfi.livePrices.class',
  // Where this person is in Get Started (lib/getStarted.ts, 2026-10-21).
  'copyfi.getStarted',
  // Lessons already read (components/engagement/Lessons.tsx, 2026-10-21).
  'copyfi.lessons.seen',
] as const;

// Prefixes whose keys carry a user id, so they cannot be listed by name.
// copyfi.lastGood.*: the last good traders list, figures and open book, shown
// during a backend restart (lib/lastGood.ts). It is this person's view, so it
// goes at sign-out with the rest (2026-10-21).
// copyfi.onboard.user: the welcome choice and how far its guide got (lib/onboarding.ts).
export const PERSONAL_KEY_PREFIXES = ['copyfi.activeCredential', 'copyfi.lastGood.', 'copyfi.onboard.user'] as const;

// Preferences of this browser, not of the person. Survive sign-out.
export const DEVICE_KEYS = [
  // Sound on wins and milestones (lib/celebrate.core.ts, 2026-10-21): like a
  // volume, a choice of this device.
  'copyfi.sound.wins',
  // This device stopped PostHog (lib/posthog.core.ts, Settings, 2026-10-22).
  'copyfi.analytics.optOut',
  // The interface language, chosen in the sidebar (i18n/index.ts). Kept
  // across sign-out: the next person at this screen reads the same language.
  'copyfi.language',
  // The Orb's one-time interview dot was seen (CopyFiOrb.tsx). Like a dismissed
  // banner: nothing about the account, so it survives sign-out.
  'copyfi.orb.inviteSeen',
  // Shows languages not yet released (Arabic before its RTL check). Opt-in, per browser.
  'copyfi.i18n.preview',
  // Shows the welcome by intent before it is on for everyone (lib/onboarding.ts). Opt-in, per browser.
  'copyfi.onboarding.preview',
  // A referral code from a ?ref= link or typed under sign-up, until the
  // account it belongs to exists (lib/referral.ts). Belongs to nobody yet.
  'copyfi.referral.pending',
  // An AI app's sign-in page, kept across the CopyFi sign-in it needed
  // (App.tsx, ConsentRoute). sessionStorage, ten minutes, read once.
  'copyfi.oauthReturn',
  // When this tab last reloaded onto a new build after a missing page chunk
  // (main.tsx, vite:preloadError). sessionStorage; nothing about the account.
  'copyfi.chunkReloadAt',
  'copyfi.orb.size',
  'copyfi-orb-dinged',
  'copyfi.livePrices.rows',
  'copyfi.phemexReferralBanner.dismissed',
  // Which columns the table shows. A layout choice of this screen, not a fact
  // about the account.
  'copyfi.topTraders.columns',
] as const;

/**
 * forgetPersonalBrowserState removes everything the signed-out person left
 * behind in this browser.
 *
 * Never throws: it runs inside sign-out, and a storage error (private mode, a
 * blocked origin) must not leave someone signed in because their browser
 * refused to forget a chat transcript.
 */
export function forgetPersonalBrowserState(): void {
  try {
    for (const key of PERSONAL_KEYS) {
      localStorage.removeItem(key);
    }
    // The prefixed ones are enumerated, because the id in them belongs to the
    // person who just left and we no longer know what it was.
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && PERSONAL_KEY_PREFIXES.some((p) => key.startsWith(p))) {
        doomed.push(key);
      }
    }
    for (const key of doomed) {
      localStorage.removeItem(key);
    }
  } catch {
    /* a browser that will not forget is still a browser that signs out */
  }
}
